import { useContext } from "react";
import { Switch, Route, Redirect } from "react-router-dom";

import AuthorizedRoute from "../../authorized-route";
import AccessoryRoute from "../../accessory-route";
// import OwnerRoute from "../../owned-route.js";

import Dashboard from "../dashboard";
import TrialExpiredPage from "../trial";
import Guests from "../guests";
import Contacts from "../contacts";
import Messages from "../messages";
import DestinationGuide from "../destination_guide";
import DestinationGuideDetail from "../destination_guide/destination_detail";
import PropertyGuide from "../property_guide";
import AddOnEditor from "../add_on/index.js";
import Scheduler from "../scheduler";
import Alerts from "../alerts";
import SavedResponses from "../saved_responses";
import MessageTemplates from "../message_templates";
import ExtendGuestStay from "../extend_guest_stay";
import Orders from "../orders";
import Payments from "../payments";
import WorkOrder from "../work_order";
import WorkOrderCalendar from "../work_order/work_order_calendar";
import WorkOrderUpdate from "../work_order/work_order_update";
import Tasks from "../operations/tasks";
import Surveys from "../surveys";
import Reports from "../reports";
import Notes from "../notes";
import Team from "../team";
import UserGroups from "../user_groups";
import Units from "../units";
import UnitDetail from "../units/unit_detail";
import Macros from "../macros";
import PropertyFieldValues from "../macros/property_field_values";
import ChatbotFaqs from "../chatbot_faqs";
import ChatbotUnansweredQuestions from "../chatbot_unanswered_questions";
import Settings from "../settings";
import Profile from "../profile";
import Billing from "../billing";
import NewSubscription from "../billing/new_subscription";
import GuestEmails from "../subscriptions";
import MessageComposer from "../common/AIMessageComposer/index.js";
import Onboarding from "../onboarding";
import GettingStarted from "../getting_started";
import WhatsNew from "../whats_new";
import FeatureDiscovery from "../features";
import OpsDashboard from "../operations";
import OpsChecklists from "../operations/checklists";
import OpsInspections from "../operations/inspections";
import OpsStorageLocations from "../operations/storage/locations";
import OpsStorageItems from "../operations/storage/items";
import OpsStorageStock from "../operations/storage/stock";
import OpsStorageMovements from "../operations/storage/movements";
import OpsInventoryHealth from "../operations/storage/health";
import OpsInventoryValueReport from "../operations/storage/reports/value";
import OpsInventoryLossReport from "../operations/storage/reports/loss";
import OpsLowStockReport from "../operations/storage/reports/low_stock";
import OpsStandardsStructure from "../operations/standards/standards_structure";
import OpsPropertySetup from "../operations/property_setup";
import Holidays from "../holidays";
import TodaysBookings from "../guests/todays_bookings.js";
import Zones from "../zones/index.js";
import WorkSchedules from "../team/work_schedules";
import AvailabilityCalendar from "../team/availability_calendar";
import CRM from "../crm";
import CRMOpenTasks from "../crm/open-tasks";
import CRMPipelines from "../crm/pipelines";
import CustomFields from "../settings/CustomFields";
import LockManagementPortal from "../lock_management_portal";
import { AppContexts } from "../../providers";
import GuideDesigner from "../guide_designer";
import FEATURES from "../../config/features";

const OPS_TASK_WORK_ORDER_CONFIG = {
  pageTitle: "Tasks",
  documentTitle: "Tasks - RueBaRue",
  itemLabel: "Task",
  itemLabelPlural: "Tasks",
  addTextLineFeature: "Tasks",
  emptyStateLabel: "tasks",
  detailBasePath: "/operations/tasks",
  showTaskType: true,
  showRecurrence: true,
  showPropertyManagerFilter: true,
  taskTypeLabel: "Type*",
};

const OperationsTaskWorkOrder = (props) => (
  <WorkOrder {...props} listConfig={OPS_TASK_WORK_ORDER_CONFIG} />
);

const RedirectToInventoryOverview = () => <Redirect to="/operations/storage" />;
const RedirectToWorkOrders = () => <Redirect to="/work-orders" />;
const RedirectToOperations = () => <Redirect to="/operations" />;

const AppLayout = (props) => {
  const { user: account } = useContext(AppContexts.AuthContext);
  const hasFlag = (flagName) => {
    const accountFlags = account?.flags || [];
    return accountFlags.some((f) => f.name === flagName);
  };
  // Tasks is now generally available for every account. The legacy Work Order
  // routes/components are intentionally kept below (the /work-orders route just
  // redirects to /operations/tasks) so old links/bookmarks keep working and we
  // retain a fast rollback path: set this back to hasFlag("ops-beta").
  const hasOpsTasks = true;

  return (
    <Switch>
      {hasOpsTasks && (
        <Route
          exact
          path="/tasks/calendar"
          render={({ location }) => (
            <Redirect to={{ pathname: "/operations/calendar", search: location.search }} />
          )}
        />
      )}
      <AuthorizedRoute path="/tasks" component={Tasks} />
      {!account && (
        <Route
          exact
          path="/work-orders/:slug/:id"
          component={WorkOrderUpdate}
        />
      )}
      {!hasOpsTasks && <Route path="/work-orders" component={WorkOrder} />}
      {hasOpsTasks && (
        <Route
          path="/work-orders"
          render={({ location }) => (
            <Redirect
              to={{
                pathname: location.pathname.replace(/^\/work-orders/, "/operations/tasks"),
                search: location.search,
              }}
            />
          )}
        />
      )}
      <Route path="/expired" component={TrialExpiredPage} />

      <AuthorizedRoute exact path="/" component={Dashboard} />
      <AuthorizedRoute path="/guests" component={Guests} />
      <AuthorizedRoute path="/contacts" component={Contacts} />
      <AuthorizedRoute path="/messages" component={Messages} />
      <AuthorizedRoute path="/properties" component={PropertyGuide} />
      <AuthorizedRoute path="/areas" component={DestinationGuide} />
      <AuthorizedRoute
        path="/areas_detail/:destination_id"
        component={DestinationGuideDetail}
      />
      <Route
        exact
        path="/master-home-guide"
        render={() => <Redirect to="/guide-designer?section=home" />}
      />
      <AccessoryRoute path="/extras" component={AddOnEditor} />
      <AuthorizedRoute path="/subscriptions" component={GuestEmails} />
      <AuthorizedRoute path="/scheduler" component={Scheduler} />
      <AuthorizedRoute path="/alerts" component={Alerts} />
      <AuthorizedRoute path="/saved-responses" component={SavedResponses} />
      <AuthorizedRoute path="/message-templates" component={MessageTemplates} />
      <AuthorizedRoute path="/extend-guest-stay" component={ExtendGuestStay} />
      <AccessoryRoute path="/orders" component={Orders} />
      <AccessoryRoute path="/payments" component={Payments} />
      <AuthorizedRoute path="/surveys" component={Surveys} />
      <AuthorizedRoute path="/reports" component={Reports} />
      <AuthorizedRoute path="/notes" component={Notes} />
      <AuthorizedRoute path="/profile" component={Profile} />
      <AuthorizedRoute path="/new-subscription" component={NewSubscription} />
      <AuthorizedRoute path="/settings" component={Settings} />
      <AuthorizedRoute path="/team" component={Team} />
      <AuthorizedRoute path="/user-groups" component={UserGroups} />
      <AuthorizedRoute path="/units" component={Units} />
      <AuthorizedRoute path="/unit_detail/:rental_id" component={UnitDetail} />
      <AuthorizedRoute path="/macros" component={Macros} />
      {hasFlag("crm") && (
        <AuthorizedRoute path="/custom-fields" component={CustomFields} />
      )}
      <AuthorizedRoute path="/property-field-values" component={PropertyFieldValues} />
      <AuthorizedRoute path="/chatbot-faqs" component={ChatbotFaqs} />
      <AuthorizedRoute
        path="/unanswered-questions"
        component={ChatbotUnansweredQuestions}
      />
      <AuthorizedRoute path="/billing" component={Billing} />
      <AuthorizedRoute path="/ai-assistant" component={MessageComposer} />
      <AuthorizedRoute path="/onboarding" component={Onboarding} />
      <AuthorizedRoute path="/getting-started" component={GettingStarted} />
      <AuthorizedRoute path="/whats-new" component={WhatsNew} />
      <AuthorizedRoute exact path="/features/:featureId?" component={FeatureDiscovery} />
      <AuthorizedRoute exact path="/operations" component={OpsDashboard} />
      <AuthorizedRoute path="/operations/checklists" component={OpsChecklists} />
      <AuthorizedRoute path="/operations/inspection-board" component={OpsInspections} />
      <AuthorizedRoute exact path="/operations/storage" component={OpsInventoryHealth} />
      <AuthorizedRoute exact path="/operations/storage/health" component={RedirectToInventoryOverview} />
      <AuthorizedRoute path="/operations/storage/locations" component={OpsStorageLocations} />
      <AuthorizedRoute path="/operations/storage/items" component={OpsStorageItems} />
      <AuthorizedRoute path="/operations/storage/stock" component={OpsStorageStock} />
      <AuthorizedRoute path="/operations/storage/movements" component={OpsStorageMovements} />
      <AuthorizedRoute path="/operations/storage/reports/value" component={OpsInventoryValueReport} />
      <AuthorizedRoute path="/operations/storage/reports/loss" component={OpsInventoryLossReport} />
      <AuthorizedRoute path="/operations/storage/reports/low-stock" component={OpsLowStockReport} />
      {hasOpsTasks && FEATURES.CLEANING_STANDARDS_ENABLED && (
        <AuthorizedRoute path="/operations/standards-structure" component={OpsStandardsStructure} />
      )}
      {hasOpsTasks && FEATURES.PROPERTY_SETUP_ENABLED && (
        <AuthorizedRoute path="/operations/property-setup" component={OpsPropertySetup} />
      )}
      {hasOpsTasks && (
        <AuthorizedRoute path="/operations/tasks" component={OperationsTaskWorkOrder} />
      )}
      {hasOpsTasks && (
        <AuthorizedRoute exact path="/operations/calendar" component={WorkOrderCalendar} />
      )}
      {!hasOpsTasks && (
        <AuthorizedRoute path="/operations/tasks" component={RedirectToWorkOrders} />
      )}
      {!hasOpsTasks && (
        <AuthorizedRoute exact path="/operations/calendar" component={RedirectToWorkOrders} />
      )}
      {(!hasOpsTasks || !FEATURES.CLEANING_STANDARDS_ENABLED) && (
        <AuthorizedRoute path="/operations/standards-structure" component={RedirectToOperations} />
      )}
      {(!hasOpsTasks || !FEATURES.PROPERTY_SETUP_ENABLED) && (
        <AuthorizedRoute path="/operations/property-setup" component={RedirectToOperations} />
      )}
      <AuthorizedRoute path="/zones" component={Zones} />
      <AuthorizedRoute path="/todays_bookings" component={TodaysBookings} />
      <AuthorizedRoute path="/holidays" component={Holidays} />
      <AuthorizedRoute path="/work-schedules" component={WorkSchedules} />
      <AuthorizedRoute path="/availability-calendar" component={AvailabilityCalendar} />
      <AuthorizedRoute exact path="/crm/open-tasks" component={CRMOpenTasks} />
      <AuthorizedRoute path="/crm/pipelines/:pipelineType?" component={CRMPipelines} />
      <AuthorizedRoute path="/crm/:category/:id(\d+)/:subpage?" component={CRM} />
      <AuthorizedRoute path="/crm/:category?" component={CRM} />
      <AuthorizedRoute path="/smart-locks" component={LockManagementPortal} />
      <AuthorizedRoute path="/guide-designer" component={GuideDesigner} />
    </Switch>
  );
};

export default AppLayout;
